import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API, getToken } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, SendHorizonal, Loader2, ClipboardCheck, Square, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

// Ephemeral by design: the transcript lives ONLY in component state, and each
// request carries the turns it needs (GAPS R13 — the server keeps no session).
// Closing the panel clears it, and there is nothing server-side to clean up.

const SUGGESTIONS = {
  nurse: [
    'What makes an FMLA packet bounce back from an employer?',
    'How should I document intermittent leave for flare-ups?',
    'What belongs in the functional capacity section?',
  ],
  doctor: [
    'What am I attesting to when I sign an FMLA certification?',
    'How specific should work restrictions be?',
    'When is an ADA accommodation note needed on top of FMLA?',
  ],
  admin: [
    'How do seat charges work on our plan?',
    'How do I import our procedure catalog from a CSV?',
    'What does the patient see after scanning our intake QR code?',
  ],
  owner: [
    'What should I check before switching Stripe to live mode?',
    'How does Demo Mode affect emails and logins?',
    'What are the HIPAA steps before real patient data?',
  ],
};

// Minimal renderer: paragraphs + **bold** — the assistant is instructed to
// answer in plain text with "-" bullets, so this covers everything it emits.
function renderText(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

function Bubble({ role, text, streaming }) {
  const isUser = role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        data-testid={`assistant-msg-${role}`}
        className={cn(
          'max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-6 whitespace-pre-wrap break-words',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-accent/60 text-foreground border rounded-bl-md'
        )}
      >
        {renderText(text)}
        {streaming && <span className="inline-block w-2 h-4 ml-0.5 align-text-bottom bg-primary/60 animate-pulse rounded-sm" />}
      </div>
    </div>
  );
}

export function AssistantPanel() {
  const { role } = useApp();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', text, reviewId?}
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const messagesRef = useRef([]); // latest transcript, readable from stream()
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // Case context: offer "Check this case" on nurse/doctor detail pages
  const caseMatch = location.pathname.match(/^\/(?:nurse\/submission|doctor\/review)\/([\w-]+)/);
  const caseId = caseMatch ? caseMatch[1] : '';

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
    scrollToEnd();
  }, [messages, scrollToEnd]);

  const resetConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setStreaming(false);
  }, []);

  // Ephemeral: closing the panel discards everything
  const onOpenChange = (next) => {
    setOpen(next);
    if (!next) resetConversation();
  };

  const stream = useCallback(async (body, shownUserText, reviewId = '') => {
    // The turns already on screen ARE the conversation the server will see.
    // A work check travels as its case id — the server rebuilds the
    // de-identified snapshot itself, so no case data goes back over the wire.
    const history = messagesRef.current
      .filter((m) => m.text || m.reviewId)
      .slice(-20)
      .map((m) => (m.reviewId ? { role: 'user', review_submission_id: m.reviewId } : { role: m.role, text: m.text }));
    setMessages((m) => [...m, { role: 'user', text: shownUserText, reviewId }, { role: 'assistant', text: '' }]);
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;

    const appendDelta = (delta) =>
      setMessages((m) => {
        const out = [...m];
        out[out.length - 1] = { ...out[out.length - 1], text: out[out.length - 1].text + delta };
        return out;
      });

    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: 'POST',
        // Raw fetch (streaming), so the axios defaults don't apply: the session
        // cookie that a bound token is checked against must be sent explicitly.
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ ...body, history }),
        signal: controller.signal,
      });
      if (!res.ok) {
        let detail = 'The assistant is unavailable right now.';
        try { detail = (await res.json())?.detail || detail; } catch { /* keep default */ }
        appendDelta(detail);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop(); // keep incomplete tail
        for (const evt of events) {
          const line = evt.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          let data;
          try { data = JSON.parse(line.slice(6)); } catch { continue; }
          if (data.delta) appendDelta(data.delta);
          if (data.error) appendDelta(`\n\nSomething went wrong: ${data.error}`);
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        appendDelta('\n\nConnection lost — please try again.');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, []);

  const send = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput('');
    stream({ message: msg }, msg);
  };

  const checkCase = () => {
    if (!caseId || streaming) return;
    stream({ action: 'review', submission_id: caseId }, 'Check this case for completeness and consistency.', caseId);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = SUGGESTIONS[role] || SUGGESTIONS.admin;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => setOpen(true)}
        data-testid="assistant-open-btn"
        aria-label="Open AI assistant"
      >
        <Bot className="h-4 w-4 text-primary" />
        <span className="hidden sm:inline">Assistant</span>
      </Button>

      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:w-[440px] sm:max-w-[440px] p-0 flex flex-col" data-testid="assistant-panel">
          <SheetHeader className="px-4 py-3 border-b space-y-0.5">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Bot className="h-4 w-4 text-primary" /> Staff Assistant
              <Badge variant="outline" className="text-[10px] font-normal">Claude Sonnet 4.5</Badge>
            </SheetTitle>
            <SheetDescription className="text-xs">
              Ephemeral — this conversation is not saved and clears when you close the panel.
            </SheetDescription>
          </SheetHeader>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="assistant-transcript">
            {messages.length === 0 ? (
              <div className="space-y-3 pt-2">
                <p className="text-sm text-muted-foreground">
                  Ask about FMLA / STD / LTD / ADA paperwork, how to use the platform — or have me double-check a case before it moves forward.
                </p>
                {caseId && (
                  <Button variant="secondary" size="sm" className="w-full justify-start gap-2" onClick={checkCase} data-testid="assistant-check-case-btn">
                    <ClipboardCheck className="h-4 w-4 text-primary" /> Check this case for issues
                  </Button>
                )}
                <div className="space-y-1.5">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      data-testid="assistant-suggestion-chip"
                      className="w-full text-left text-xs rounded-lg border bg-card px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <Bubble key={i} role={m.role} text={m.text} streaming={streaming && i === messages.length - 1 && m.role === 'assistant'} />
              ))
            )}
          </div>

          <div className="border-t px-3 py-3 space-y-2 bg-background">
            {messages.length > 0 && (
              <div className="flex items-center gap-2">
                {caseId && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={checkCase} disabled={streaming} data-testid="assistant-check-case-btn">
                    <ClipboardCheck className="h-3.5 w-3.5" /> Check this case
                  </Button>
                )}
                <div className="flex-1" />
                {streaming ? (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => abortRef.current?.abort()} data-testid="assistant-stop-btn">
                    <Square className="h-3 w-3" /> Stop
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1 text-muted-foreground" onClick={() => resetConversation()} data-testid="assistant-clear-btn">
                    <RotateCcw className="h-3 w-3" /> Clear
                  </Button>
                )}
              </div>
            )}
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask the assistant…"
                rows={1}
                className="min-h-[44px] max-h-32 resize-none text-sm"
                data-testid="assistant-input"
              />
              <Button
                size="icon"
                className="h-11 w-11 shrink-0"
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                data-testid="assistant-send-btn"
                aria-label="Send message"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
